/** Category-driven search filter field definitions for admin create/edit forms. */

export type SearchCategoryId = 'vehicles' | 'apartments' | 'land' | 'hot_topics';

export type SearchFilterFieldType = 'text' | 'number' | 'select' | 'textarea' | 'catalog';

export type VehicleCatalogKind = 'make' | 'model' | 'trim';

export type SearchFilterFieldGroup =
  | 'keywords'
  | 'location'
  | 'property'
  | 'details'
  | 'advanced'
  | 'pricing';

export type SearchFilterFormKey =
  | 'search_text'
  | 'brand'
  | 'model'
  | 'trim'
  | 'body_type'
  | 'generation'
  | 'year_min'
  | 'year_max'
  | 'price_min'
  | 'price_max'
  | 'mileage_min'
  | 'mileage_max'
  | 'fuel'
  | 'transmission'
  | 'message_templates'
  | 'city'
  | 'region'
  | 'property_type'
  | 'listing_type'
  | 'sqm_min'
  | 'sqm_max'
  | 'rooms_min'
  | 'rooms_max'
  | 'floor_min'
  | 'floor_max'
  | 'vehicle_make_id'
  | 'vehicle_model_id'
  | 'vehicle_trim_id';

export type SearchFilterFieldDef = {
  key: SearchFilterFormKey;
  filterKey: string;
  label: string;
  type: SearchFilterFieldType;
  group: SearchFilterFieldGroup;
  order: number;
  placeholder?: string;
  hint?: string;
  gridClass?: string;
  options?: { value: string; label: string }[];
  /** Pair id for min/max validation (e.g. year, sqm, price). */
  minMaxPair?: string;
  serializeAs?: 'string' | 'number' | 'stringList';
  catalogKind?: VehicleCatalogKind;
  catalogIdKey?: SearchFilterFormKey;
};

export type SearchCategoryDef = {
  id: SearchCategoryId;
  label: string;
  fields: SearchFilterFieldDef[];
};

const GROUP_LABELS: Record<SearchFilterFieldGroup, string> = {
  keywords: 'Keywords',
  location: 'Location',
  property: 'Property',
  details: 'Details',
  advanced: 'Advanced',
  pricing: 'Price',
};

const GROUP_ORDER: SearchFilterFieldGroup[] = [
  'keywords',
  'location',
  'property',
  'details',
  'advanced',
  'pricing',
];

const PRICE_FIELDS: SearchFilterFieldDef[] = [
  {
    key: 'price_min',
    filterKey: 'price_min',
    label: 'Price min (EUR)',
    type: 'number',
    group: 'pricing',
    order: 1,
    minMaxPair: 'price',
    serializeAs: 'number',
  },
  {
    key: 'price_max',
    filterKey: 'price_max',
    label: 'Price max (EUR)',
    type: 'number',
    group: 'pricing',
    order: 2,
    minMaxPair: 'price',
    serializeAs: 'number',
  },
];

const VEHICLE_FIELDS: SearchFilterFieldDef[] = [
  {
    key: 'search_text',
    filterKey: 'search_text',
    label: 'Search text',
    type: 'text',
    group: 'keywords',
    order: 1,
    gridClass: 'search-form__search-text',
    placeholder: 'Honda Civic Type R',
    serializeAs: 'string',
  },
  {
    key: 'brand',
    filterKey: 'brand',
    label: 'Make',
    type: 'catalog',
    catalogKind: 'make',
    catalogIdKey: 'vehicle_make_id',
    group: 'details',
    order: 1,
    placeholder: 'Abarth',
    serializeAs: 'string',
  },
  {
    key: 'model',
    filterKey: 'model',
    label: 'Model / Series',
    type: 'catalog',
    catalogKind: 'model',
    catalogIdKey: 'vehicle_model_id',
    group: 'details',
    order: 2,
    placeholder: '500',
    serializeAs: 'string',
  },
  {
    key: 'trim',
    filterKey: 'trim',
    label: 'Trim / Variant',
    type: 'catalog',
    catalogKind: 'trim',
    catalogIdKey: 'vehicle_trim_id',
    group: 'advanced',
    order: 1,
    placeholder: '595 Turismo',
    serializeAs: 'string',
  },
  {
    key: 'body_type',
    filterKey: 'body_type',
    label: 'Body type',
    type: 'select',
    group: 'advanced',
    order: 2,
    options: [
      { value: '', label: 'Any' },
      { value: 'coupe', label: 'Coupé' },
      { value: 'cabrio', label: 'Cabrio' },
      { value: 'hatchback', label: 'Hatchback' },
      { value: 'limousine', label: 'Limousine' },
      { value: 'small_car', label: 'Small car' },
    ],
    serializeAs: 'string',
  },
  {
    key: 'generation',
    filterKey: 'generation',
    label: 'Generation',
    type: 'text',
    group: 'advanced',
    order: 3,
    placeholder: '150',
    serializeAs: 'string',
  },
  {
    key: 'year_min',
    filterKey: 'year_min',
    label: 'Year min',
    type: 'number',
    group: 'details',
    order: 5,
    minMaxPair: 'year',
    serializeAs: 'number',
  },
  {
    key: 'year_max',
    filterKey: 'year_max',
    label: 'Year max',
    type: 'number',
    group: 'details',
    order: 6,
    minMaxPair: 'year',
    serializeAs: 'number',
  },
  {
    key: 'mileage_min',
    filterKey: 'mileage_min',
    label: 'Mileage min (km)',
    type: 'number',
    group: 'details',
    order: 7,
    minMaxPair: 'mileage',
    serializeAs: 'number',
  },
  {
    key: 'mileage_max',
    filterKey: 'mileage_max',
    label: 'Mileage max (km)',
    type: 'number',
    group: 'details',
    order: 8,
    minMaxPair: 'mileage',
    serializeAs: 'number',
  },
  {
    key: 'fuel',
    filterKey: 'fuel',
    label: 'Fuel',
    type: 'text',
    group: 'details',
    order: 9,
    placeholder: 'petrol, diesel, electric…',
    serializeAs: 'string',
  },
  {
    key: 'transmission',
    filterKey: 'transmission',
    label: 'Transmission',
    type: 'text',
    group: 'details',
    order: 10,
    placeholder: 'manual, automatic…',
    serializeAs: 'string',
  },
  {
    key: 'message_templates',
    filterKey: 'message_templates',
    label: 'Message templates',
    type: 'textarea',
    group: 'details',
    order: 11,
    gridClass: 'search-form__templates',
    hint: 'one per line',
    placeholder: 'Check this out!',
    serializeAs: 'stringList',
  },
];

const APARTMENT_FIELDS: SearchFilterFieldDef[] = [
  {
    key: 'search_text',
    filterKey: 'search_text',
    label: 'Search text',
    type: 'text',
    group: 'keywords',
    order: 1,
    gridClass: 'search-form__search-text',
    placeholder: 'apartment, house…',
    serializeAs: 'string',
  },
  {
    key: 'city',
    filterKey: 'city',
    label: 'City',
    type: 'text',
    group: 'location',
    order: 1,
    placeholder: 'Split',
    serializeAs: 'string',
  },
  {
    key: 'region',
    filterKey: 'region',
    label: 'Region',
    type: 'text',
    group: 'location',
    order: 2,
    placeholder: 'Dalmatia',
    serializeAs: 'string',
  },
  {
    key: 'listing_type',
    filterKey: 'listing_type',
    label: 'Listing type',
    type: 'select',
    group: 'property',
    order: 1,
    options: [
      { value: '', label: 'Any' },
      { value: 'rent', label: 'Rent' },
      { value: 'sale', label: 'Sale' },
    ],
    serializeAs: 'string',
  },
  {
    key: 'property_type',
    filterKey: 'property_type',
    label: 'Property type',
    type: 'select',
    group: 'property',
    order: 2,
    options: [
      { value: '', label: 'Any' },
      { value: 'apartment', label: 'Apartment' },
      { value: 'house', label: 'House' },
    ],
    serializeAs: 'string',
  },
  {
    key: 'rooms_min',
    filterKey: 'rooms_min',
    label: 'Rooms min',
    type: 'number',
    group: 'property',
    order: 3,
    minMaxPair: 'rooms',
    serializeAs: 'number',
  },
  {
    key: 'rooms_max',
    filterKey: 'rooms_max',
    label: 'Rooms max',
    type: 'number',
    group: 'property',
    order: 4,
    minMaxPair: 'rooms',
    serializeAs: 'number',
  },
  {
    key: 'sqm_min',
    filterKey: 'sqm_min',
    label: 'Area m² min',
    type: 'number',
    group: 'property',
    order: 5,
    minMaxPair: 'sqm',
    serializeAs: 'number',
  },
  {
    key: 'sqm_max',
    filterKey: 'sqm_max',
    label: 'Area m² max',
    type: 'number',
    group: 'property',
    order: 6,
    minMaxPair: 'sqm',
    serializeAs: 'number',
  },
  {
    key: 'floor_min',
    filterKey: 'floor_min',
    label: 'Floor min',
    type: 'number',
    group: 'property',
    order: 7,
    minMaxPair: 'floor',
    serializeAs: 'number',
  },
  {
    key: 'floor_max',
    filterKey: 'floor_max',
    label: 'Floor max',
    type: 'number',
    group: 'property',
    order: 8,
    minMaxPair: 'floor',
    serializeAs: 'number',
  },
];

const LAND_FIELDS: SearchFilterFieldDef[] = [
  {
    key: 'search_text',
    filterKey: 'search_text',
    label: 'Search text',
    type: 'text',
    group: 'keywords',
    order: 1,
    gridClass: 'search-form__search-text',
    placeholder: 'keywords…',
    serializeAs: 'string',
  },
  {
    key: 'sqm_min',
    filterKey: 'sqm_min',
    label: 'Area m² min',
    type: 'number',
    group: 'property',
    order: 1,
    minMaxPair: 'sqm',
    serializeAs: 'number',
  },
  {
    key: 'sqm_max',
    filterKey: 'sqm_max',
    label: 'Area m² max',
    type: 'number',
    group: 'property',
    order: 2,
    minMaxPair: 'sqm',
    serializeAs: 'number',
  },
];

const GENERIC_FIELDS: SearchFilterFieldDef[] = [
  {
    key: 'search_text',
    filterKey: 'search_text',
    label: 'Search text',
    type: 'text',
    group: 'keywords',
    order: 1,
    gridClass: 'search-form__search-text',
    placeholder: 'keywords…',
    serializeAs: 'string',
  },
];

export const SEARCH_CATEGORY_DEFS: Record<SearchCategoryId, SearchCategoryDef> = {
  vehicles: { id: 'vehicles', label: 'Vehicle', fields: [...VEHICLE_FIELDS, ...PRICE_FIELDS] },
  apartments: { id: 'apartments', label: 'Apartment', fields: [...APARTMENT_FIELDS, ...PRICE_FIELDS] },
  land: { id: 'land', label: 'Land', fields: [...LAND_FIELDS, ...PRICE_FIELDS] },
  hot_topics: { id: 'hot_topics', label: 'Generic', fields: [...GENERIC_FIELDS] },
};

const MIN_MAX_PAIR_LABELS: Record<string, { min: string; max: string }> = {
  year: { min: 'Year min', max: 'Year max' },
  rooms: { min: 'Rooms min', max: 'Rooms max' },
  sqm: { min: 'Area min', max: 'Area max' },
  floor: { min: 'Floor min', max: 'Floor max' },
  price: { min: 'Price min', max: 'Price max' },
  mileage: { min: 'Mileage min', max: 'Mileage max' },
};

export function getSearchCategoryDef(categoryId: string): SearchCategoryDef | null {
  return SEARCH_CATEGORY_DEFS[categoryId as SearchCategoryId] ?? null;
}

export function isKnownSearchCategory(categoryId: string): categoryId is SearchCategoryId {
  return categoryId in SEARCH_CATEGORY_DEFS;
}

export function getFilterFieldKeys(categoryId: string): SearchFilterFormKey[] {
  const def = getSearchCategoryDef(categoryId);
  if (!def) return ['search_text', 'price_min', 'price_max'];
  return def.fields.map((f) => f.key);
}

export function getGroupedFields(categoryId: string): { group: SearchFilterFieldGroup; label: string; fields: SearchFilterFieldDef[] }[] {
  const def = getSearchCategoryDef(categoryId);
  if (!def) return [];

  const byGroup = new Map<SearchFilterFieldGroup, SearchFilterFieldDef[]>();
  for (const field of def.fields) {
    const list = byGroup.get(field.group) ?? [];
    list.push(field);
    byGroup.set(field.group, list);
  }

  return GROUP_ORDER.filter((g) => byGroup.has(g)).map((group) => ({
    group,
    label: GROUP_LABELS[group],
    fields: (byGroup.get(group) ?? []).sort((a, b) => a.order - b.order),
  }));
}

export function getMinMaxPairLabels(pairId: string): { min: string; max: string } {
  return MIN_MAX_PAIR_LABELS[pairId] ?? { min: 'Min', max: 'Max' };
}

export function isVehicleCategory(category: string): boolean {
  return category === 'vehicles';
}

export function isApartmentCategory(category: string): boolean {
  return category === 'apartments';
}

export function isLandCategory(category: string): boolean {
  return category === 'land';
}

export function isGenericCategory(category: string): boolean {
  return category === 'hot_topics';
}
